import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon, BikeIcon, BookOpenIcon, BriefcaseIcon, CheckIcon, HomeIcon, PenToolIcon, ShoppingBagIcon, UtensilsCrossedIcon, WrenchIcon } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { Panel } from '../../../components/ui/Panel';
import { Badge } from '../../../components/ui/Badge';

/**
 * The second side of the marketplace. What you intend to offer decides what you
 * must verify before you can list — so the first question is that, not a form.
 */

interface OfferType {
  id: string;
  label: string;
  detail: string;
  icon: typeof ShoppingBagIcon;
  /** What must be established before a listing can go live. */
  requirements: string[];
  /** Stated plainly, because it decides whether this is worth starting. */
  reviewNote: string;
}
const OFFER_TYPES: OfferType[] = [{
  id: 'products',
  label: 'Products',
  detail: 'Electronics, clothing, accessories, essentials.',
  icon: ShoppingBagIcon,
  requirements: ['Verified Campuzon account', 'Campus confirmed'],
  reviewNote: 'Listings go live immediately. Repeated reports pause the account for review.'
}, {
  id: 'food',
  label: 'Food and groceries',
  detail: 'Meals, snacks, drinks, groceries.',
  icon: UtensilsCrossedIcon,
  requirements: ['Verified Campuzon account', 'Business or vendor name', 'Preparation address', 'Any food handling permit your campus or local authority requires'],
  reviewNote: 'Reviewed by a person before your first listing. Campuzon does not inspect kitchens and does not certify food safety.'
}, {
  id: 'books',
  label: 'Books and supplies',
  detail: 'Textbooks, past questions, stationery, lab materials.',
  icon: BookOpenIcon,
  requirements: ['Verified Campuzon account'],
  reviewNote: 'Listings go live immediately. Selling copyrighted material you do not hold the right to distribute is removed.'
}, {
  id: 'accommodation',
  label: 'Accommodation',
  detail: 'Hostels, apartments, rooms.',
  icon: HomeIcon,
  requirements: ['Verified identity', 'Evidence you are the owner or the appointed agent', 'Property address'],
  reviewNote: 'Reviewed by a person, and the property is separately marked as inspected or not inspected. An unverified listing can still be published, labelled as such.'
}, {
  id: 'transport',
  label: 'Transport',
  detail: 'Campus rides, moving, delivery, logistics.',
  icon: BikeIcon,
  requirements: ['Verified identity', 'Driving licence', 'Vehicle details and insurance'],
  reviewNote: 'Reviewed by a person. Campuzon checks that the documents exist and match you. It does not inspect vehicles.'
}, {
  id: 'gigs',
  label: 'Student gigs',
  detail: 'Tutoring, design, photography, writing, technical work.',
  icon: PenToolIcon,
  requirements: ['Verified student standing'],
  reviewNote: 'Live immediately. Academic work must be help, not substitution — writing something for another student to submit is refused and recorded.'
}, {
  id: 'services',
  label: 'Technical and repair services',
  detail: 'CAD, programming, electronics, engineering, repairs.',
  icon: WrenchIcon,
  requirements: ['Verified identity', 'Description of what you are qualified to do'],
  reviewNote: 'Reviewed by a person. Campuzon does not assess technical competence; your reputation is built from completed orders.'
}, {
  id: 'professional',
  label: 'Professional services',
  detail: 'Accounting, marketing, consulting, business support.',
  icon: BriefcaseIcon,
  requirements: ['Verified identity', 'Business registration where the service legally requires one', 'Professional registration where the service legally requires one'],
  reviewNote: 'Reviewed by a person. Regulated services cannot be listed without the registration that regulates them.'
}];
export function BecomeProvider() {
  const [selected, setSelected] = useState<string | null>(null);
  const choice = OFFER_TYPES.find(type => type.id === selected);
  return <div className="space-y-8 pb-8" data-id="element-10509" data-mp="31rzdwk7tmor5">
      <div data-id="element-10510" data-mp="36r27n8icp8jt">
        <Link to="/market" className="inline-flex min-h-touch items-center gap-1.5 text-body-sm font-medium text-ink-muted hover:text-ink" data-id="element-10511" data-mp="36qsq89bdg9pq">
          <ArrowLeftIcon aria-hidden className="h-4 w-4" data-id="element-10512" data-mp="2sy2s56ikjec9" />
          Marketplace
        </Link>
      </div>

      <header className="max-w-[62ch]" data-id="element-10513" data-mp="2j1rg8hmw4qic">
        <h1 className="text-h1 font-semibold tracking-tight text-ink" data-id="element-10514" data-mp="2j2spqho9d4xy">
          Sell products. Offer services. Reach your campus.
        </h1>
        <p className="mt-3 text-body-lg text-ink-muted" data-id="element-10515" data-mp="2nyhtyyb83a0l">
          You do not need a second account. Your Campuzon identity gains a provider role, and what
          you must verify depends on what you intend to offer.
        </p>
      </header>

      <section aria-labelledby="offer-type" data-id="element-10516" data-mp="1vdkusgsjvhfc">
        <h2 id="offer-type" className="text-h3 font-semibold text-ink" data-id="element-10517" data-mp="1vggr60or1y1o">
          What do you want to offer?
        </h2>
        <p className="mt-1.5 text-body text-ink-muted" data-id="element-10518" data-mp="24f104a4ictcp">
          Choose one to start. You can add others later without applying again.
        </p>

        <ul className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-id="element-10519" data-mp="1zf6986kqg3rn">
          {OFFER_TYPES.map(type => {
          const Icon = type.icon;
          const active = selected === type.id;
          return <li key={type.id} data-id="element-10520" data-mp="29dvmtn5lj223">
                <button type="button" onClick={() => setSelected(type.id)} aria-pressed={active} className={`flex h-full w-full flex-col rounded-card border px-5 py-5 text-left transition-colors duration-150 ease-out ${active ? 'border-brand-700 bg-brand-50' : 'border-line bg-surface hover:border-line-strong'}`} data-id="element-10521" data-mp="2n5agqp11knnx">
                  <span className={`flex h-11 w-11 items-center justify-center rounded-card ${active ? 'bg-brand-100' : 'bg-canvas'}`} data-id="element-10522" data-mp="1tssjbr58vyjq">
                    <Icon aria-hidden className={`h-5 w-5 ${active ? 'text-brand-800' : 'text-brand-700'}`} data-id="element-10523" data-mp="23j5eseellc4n" />
                  </span>
                  <span className="mt-3.5 text-body font-semibold text-ink" data-id="element-10524" data-mp="2dga0ye8g17lg">{type.label}</span>
                  <span className="mt-1 text-body-sm text-ink-muted" data-id="element-10525" data-mp="2himxcc1avt5k">{type.detail}</span>
                </button>
              </li>;
        })}
        </ul>
      </section>

      {choice ? <Panel title={`Selling ${choice.label.toLowerCase()}`} description="What has to be established before your first listing can go live." data-id="element-10526" data-mp="n4vo3dmvvban">
          <ul className="space-y-2.5" data-id="element-10527" data-mp="s59vctgnuh7m">
            {choice.requirements.map(requirement => <li key={requirement} className="flex items-start gap-2.5 text-body text-ink" data-id="element-10528" data-mp="klqzvnq8cvi5">
                <CheckIcon aria-hidden className="mt-1 h-4 w-4 shrink-0 text-brand-700" data-id="element-10529" data-mp="frec51hq0y0l" />
                {requirement}
              </li>)}
          </ul>

          <div className="mt-5 rounded-card border border-line bg-canvas px-4 py-3.5" data-id="element-10530" data-mp="5wme5wk8895a">
            <p className="text-body-sm text-ink" data-id="element-10531" data-mp="13hqoqpnwvixi">
              <strong className="font-semibold" data-id="element-10532" data-mp="yjpaci1fk7sd">Before you start.</strong> {choice.reviewNote}
            </p>
          </div>

          <div className="mt-5 flex flex-wrap items-center gap-3" data-id="element-10533" data-mp="tk2tzzqb2bfc">
            <Button variant="primary" data-id="element-10534" data-mp="tmgqmd0mvvp0">Start provider setup</Button>
            <Badge tone="warn" plain data-id="element-10535" data-mp="pihahvj9myw6">
              Payouts are subject to settlement rules and identity checks
            </Badge>
          </div>
        </Panel> : <Panel title="What applies to every provider" data-id="element-10536" data-mp="3a8zwvbqr5e82">
          <ul className="space-y-3 text-body-sm text-ink" data-id="element-10537" data-mp="3abm6dgx7e3sx">
            {['You keep one account. The provider role is added to it, not created separately.', 'Buyers see whether Campuzon has verified you. Choosing not to verify is allowed, and is shown to them.', 'Money for services is held until the work is delivered and accepted, rather than passed straight to you.', 'Campuzon takes a stated fee per order. It is shown before you accept, never deducted silently.', 'Reputation is built from completed orders in each area of work, not from a single overall score.'].map(rule => <li key={rule} className="flex items-start gap-2.5" data-id="element-10538" data-mp="1n69un6jq8x6t">
                <span aria-hidden className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-700" data-id="element-10539" data-mp="1s5332kk5py04" />
                {rule}
              </li>)}
          </ul>
        </Panel>}
    </div>;
}